<template>

  <transition>
    <div class="vz-dialog" v-if="active" :class="'vz-dialog-type-' + type">
      <div class="vz-dialog-panel">
        <div class="vz-dialog-header">
          <h4 class="vz-dialog-title">
            <span class="vz-dialog-title-icon">
              <i :class="iconClass"></i>
            </span>
            <span class="vz-dialog-title-text">
              {{ title }}
            </span>
          </h4>
        </div>
        <div class="vz-dialog-body"><slot/></div>
        <div class="vz-dialog-footer">
          <vz-button v-for="(bt, index) in buttons" :key="index" :class="{ 'vz-primary': bt.primary }" @click="handleClick(bt)">
            <span class="vz-bt-label-txt">{{ bt.label }}</span>
          </vz-button>
        </div>
      </div>
    </div>
  </transition>

</template>

<script>
import VzButton from '../atoms/vz-button';

/**
 * Dialog panel with buttons.
 * Useful for alert messages or confirmation popups.
 * @module VzDialog
 * @param {Boolean} active
 * @param {String} title
 * @param {String} text
 * @param {String} type
 * @param {Array} buttons
 * @author Lanza
 */
export default {

  name: "VzDialog",
  components: {
    vzButton: VzButton
  },

  props: {
    active: {
      type: Boolean,
      default: false,
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'success',
      validator: value => ['alert', 'question', 'error', 'success'].indexOf(value) !== -1
    },
    buttons: {
      type: Array,
      default: () => [],    // [{ label: String, value: String, primary: Boolean, callback: Function }]
      required: true
    }
  },

  computed: {

    iconClass: function () {
      let res;
      switch(this.type) {
        case 'alert':
          res = 'ic-alert-triangle';
          break;
        case 'question':
          res = 'ic-help-circle';
          break;
        case 'error':
          res = 'ic-x-octagon';
          break;
        default:    // success
          res = 'ic-check-circle';
      }
      return res;
    }

  },

  methods: {

    handleClick: function (button) {
      if(button.callback) {
        button.callback(button)
      }
    }

  }

}
</script>

<style>

@keyframes vz-dialog-slide-up {
  0% {
    transform: translate(-50%, 110%);
  }
  100% {
    transform: translate(-50%, 0);
  }
}

@keyframes vz-dialog-slide-down {
  0% {
    transform: translate(-50%, 0);
  }
  100% {
    transform: translate(-50%, 110%);
  }
}

@keyframes vz-dialog-bump-in {
  0% {
    opacity: 0;
    transform: scale(0.8, 0.8);
  }
  80% {
    opacity: 1;
    transform: scale(1.15, 1.15);
  }
  100% {
    transform: scale(1, 1);
  }
}

@keyframes vz-dialog-fade-out {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.vz-dialog {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 99999;
  overflow: hidden;
  background-color: rgba(0,0,0,0.5);
  backdrop-filter: blur(2px);
}

.vz-dialog.v-enter-active,
.vz-dialog.v-leave-active
{
  transition-property: opacity;
  transition-duration: 0.3s;
}

.vz-dialog.v-leave-active {
  transition-delay: 0.3s;
}

.vz-dialog.v-enter,
.vz-dialog.v-enter-from,
.vz-dialog.v-leave-to
{
  opacity: 0;
}

.vz-dialog.v-enter-to,
.vz-dialog.v-leave,
.vz-dialog.v-leave-from
{
  opacity: 1;
}

.vz-dialog-panel {
  background-color: #303030;
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -8px 16px rgba(0,0,0,0.85);
  padding: 32px;
  width: 100%;
  max-width: 640px;
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 0);
  animation-name: vz-dialog-slide-up;
  animation-duration: 0.4s;
  animation-iteration-count: 1;
  animation-fill-mode: forwards;
}

.vz-dialog.v-leave-active .vz-dialog-panel {
  animation-name: vz-dialog-slide-down;
  animation-duration: 0.3s;
}

.vz-dialog-title {
  margin: 0;
  padding: 0;
  position: relative;
  font-weight: normal;
  text-align: center;
}

.vz-dialog-title-icon {
  display: block;
  margin: 0 0 8px;
  font-size: 40px;
  color: var(--primary-color);
}

.vz-dialog-title-text {
  font-size: 1.4em;
  line-height: 1.33em;
}

.vz-dialog-body {
  padding: 32px 0 0;
  text-align: center;
  color: #ffffff;
}

.vz-dialog-body:empty {
  display: none;
}

.vz-dialog-footer {
  padding: 32px 0 0;
  text-align: center;
}

.vz-dialog-footer .vz-bt {
  margin: 8px 0 0 8px;
}

.vz-dialog-footer .vz-bt:first-child {
  margin-left: 0;
}

@media (min-width: 1024px) {

  .vz-dialog {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .vz-dialog-panel {
    position: relative;
    bottom: auto;
    left: auto;
    transform: none;
    border-radius: 8px;
    max-width: 720px;
    animation-name: vz-dialog-bump-in;
  }

  .vz-dialog.v-leave-active .vz-dialog-panel {
    animation-name: vz-dialog-fade-out
  }

}

</style>